import { useEffect, useId, useRef, useState } from 'react'
import { apiGet, isApiError } from '../api/client'
import { useT } from '../i18n'
import s from './SchoolSearchPicker.module.css'

/**
 * School-name search picker (ADR 0051 Phase 4, #1091).
 *
 * Replaces the raw ``login_code`` text field on the student login form: the
 * student types their school NAME, picks it from a debounced dropdown, and we
 * hand the chosen school's ``login_code`` up to the parent via ``onChange``.
 * A "type the code instead" fallback covers the rare miss (a school whose
 * operator hasn't named it yet, an offline edge) — in that mode the typed
 * value IS the ``login_code``.
 *
 * The picker is the only thing that talks to ``GET /api/schools/search`` (a
 * public, server-side, capped search — the client never downloads the roster).
 * The parent owns the resolved ``loginCode`` string so the existing
 * student-login POST is unchanged.
 */
interface SchoolSearchResult {
  name: string
  location: string | null
  login_code: string
}

interface SchoolSearchResponse {
  results: SchoolSearchResult[]
}

interface Props {
  /** The resolved login_code (controlled by the parent). */
  loginCode: string
  /** Report the resolved login_code up — '' clears it (in-progress search). */
  onChange: (loginCode: string) => void
  inputId: string
  inputRef?: React.Ref<HTMLInputElement>
  /** Parent form is visible — drives tabIndex so hidden forms aren't tabbable. */
  formActive: boolean
  testIdPrefix?: string
}

const _MIN_QUERY_LEN = 2
const _DEBOUNCE_MS = 250

export default function SchoolSearchPicker({
  loginCode,
  onChange,
  inputId,
  inputRef,
  formActive,
  testIdPrefix = 'school-search',
}: Props) {
  const t = useT()
  const listboxId = useId()
  // ``manual`` = the "type the code instead" fallback. In manual mode the
  // input's text IS the login_code; in search mode the text is the query and
  // a selection resolves the code.
  const [manual, setManual] = useState(false)
  // Visible input text. In manual mode it mirrors ``loginCode``; in search
  // mode it's the query (or the chosen school's name after a pick).
  const [text, setText] = useState('')
  const [results, setResults] = useState<SchoolSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  // Distinguishes a TRANSIENT search failure (backend 5xx/401, a missing
  // /schools/search route, or a network blip) from a genuine zero-match
  // (#1122). The backend returns 200 + ``{results: []}`` for "no schools
  // match" — that path never throws — so anything reaching the fetch's
  // ``.catch`` is an unavailability, NOT a real empty result. Rendering the
  // same "no matching schools" hint for both stranded a first-time student
  // (retype → same message) when the service was simply down. ``'unavailable'``
  // swaps in a distinct alert that steers them to the manual-code fallback.
  const [searchError, setSearchError] = useState<'unavailable' | null>(null)
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  // True right after a pick so the open-on-results effect doesn't immediately
  // re-open the dropdown for the (now name-filled) input.
  const justPickedRef = useRef(false)

  const tabIndex = formActive ? 0 : -1

  // Debounced search. Skips in manual mode and for sub-min queries. Ignores a
  // stale response via the cleanup flag so an earlier slow request can't
  // overwrite a newer one's results.
  useEffect(() => {
    if (manual) return
    if (justPickedRef.current) {
      justPickedRef.current = false
      return
    }
    const term = text.trim()
    // Sub-min queries don't search; the dropdown is reset in ``onSearchInput``
    // (event handler) so this effect body stays free of synchronous setState
    // (react-hooks/set-state-in-effect).
    if (term.length < _MIN_QUERY_LEN) return
    let cancelled = false
    const id = window.setTimeout(() => {
      // All setState here runs in the (async) timeout callback, never
      // synchronously in the effect body. Open + loading flip on as the fetch
      // starts so the dropdown shows the "Searching…" hint during the request.
      // Clear any prior transient error (#1122) so a retry shows "Searching…",
      // not the stale "unavailable" alert, while the new request is in flight.
      setLoading(true)
      setSearchError(null)
      setOpen(true)
      apiGet<SchoolSearchResponse>(
        `/schools/search?q=${encodeURIComponent(term)}`,
      )
        .then((data) => {
          if (cancelled) return
          // searchError was already cleared at fetch start, so a success just
          // fills the results — a 200 + empty list is a genuine no-match and
          // correctly renders the noResults hint, NOT the unavailable alert.
          setResults(data.results)
          setActiveIndex(data.results.length > 0 ? 0 : -1)
          setOpen(true)
        })
        .catch((e: unknown) => {
          if (cancelled) return
          // #1122: the genuine "no schools match" case is a 200 + empty
          // ``results`` (the ``.then`` arm) — it never lands here. So
          // EVERYTHING that reaches this catch is a transient failure: a
          // backend 5xx/401, a missing route (404), or a network blip
          // (``apiGet`` rejects with a non-``ApiError`` for the last). We
          // narrow with ``isApiError`` (mirroring DevQuickstart's idiom) to
          // emit a precise OPERATOR signal — the HTTP status when we have one,
          // 'network' otherwise — but every class maps to the same USER
          // outcome: "search unavailable", never a fake no-results that
          // strands a first-time student. Surface a distinct alert + steer
          // them to the manual-code fallback. Clear the stale result list so
          // an earlier search's rows don't linger under the alert.
          const reason = isApiError(e) ? `HTTP ${e.status}` : 'network'
          console.warn(`[SchoolSearchPicker] /schools/search failed (${reason})`)
          setResults([])
          // Reset the active option (mirrors the success arm) so the combobox
          // doesn't keep advertising aria-activedescendant for an option that
          // no longer exists once the alert replaces the listbox (#1130).
          setActiveIndex(-1)
          setSearchError('unavailable')
          setOpen(true)
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }, _DEBOUNCE_MS)
    return () => {
      cancelled = true
      window.clearTimeout(id)
    }
  }, [text, manual])

  const pick = (r: SchoolSearchResult) => {
    justPickedRef.current = true
    onChange(r.login_code)
    setText(r.name)
    setResults([])
    setSearchError(null)
    setOpen(false)
    setActiveIndex(-1)
  }

  const onSearchInput = (value: string) => {
    setText(value)
    // Any edit invalidates a prior selection — clear the resolved code until
    // the student picks again (or types a full match).
    if (loginCode) onChange('')
    // Reset the dropdown for a sub-min query here (in the event handler) so the
    // search effect never has to setState synchronously in its body. Also clear
    // a stale "search unavailable" alert (#1122) — the student is re-typing, so
    // the next search gets a clean slate rather than showing the old error.
    if (value.trim().length < _MIN_QUERY_LEN) {
      setResults([])
      setSearchError(null)
      setOpen(false)
      setLoading(false)
    }
  }

  const onManualInput = (value: string) => {
    setText(value)
    onChange(value.trim())
  }

  const switchToManual = () => {
    setManual(true)
    setOpen(false)
    setResults([])
    setSearchError(null)
    setText(loginCode)
  }

  const switchToSearch = () => {
    setManual(false)
    setSearchError(null)
    setText('')
    onChange('')
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (manual || !open || results.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => (i + 1) % results.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => (i - 1 + results.length) % results.length)
    } else if (e.key === 'Enter') {
      // Intercept Enter so it picks the active option instead of submitting
      // the surrounding login form.
      if (activeIndex >= 0 && activeIndex < results.length) {
        e.preventDefault()
        pick(results[activeIndex])
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  if (manual) {
    return (
      <>
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          value={text}
          onChange={(e) => onManualInput(e.target.value)}
          placeholder={t('login.schoolCode.placeholder')}
          autoComplete="off"
          autoCapitalize="characters"
          tabIndex={tabIndex}
          data-testid={`${testIdPrefix}-manual-input`}
        />
        <button
          type="button"
          className={s.toggle}
          onClick={switchToSearch}
          tabIndex={tabIndex}
          data-testid={`${testIdPrefix}-to-search`}
        >
          {t('login.schoolSearch.searchToggle')}
        </button>
      </>
    )
  }

  return (
    <div className={s.wrap}>
      <input
        id={inputId}
        ref={inputRef}
        type="text"
        role="combobox"
        // Only claim the listbox is open when we're actually rendering it —
        // during a transient search failure (#1122) we swap the listbox for a
        // role="alert" banner, so the combobox must report collapsed.
        aria-expanded={open && !searchError}
        aria-controls={listboxId}
        aria-autocomplete="list"
        // Mirror aria-expanded's narrowing (#1130): during a transient search
        // failure the listbox is swapped for the alert, so the combobox must
        // not point at an option that's no longer rendered.
        aria-activedescendant={
          open && !searchError && activeIndex >= 0
            ? `${listboxId}-opt-${activeIndex}`
            : undefined
        }
        value={text}
        // Cap at the backend's Query(max_length=64) (#1130): a pasted >64-char
        // query would 422 and fall into the catch as a fake "search
        // unavailable" — the input is just too long, the service is fine.
        maxLength={64}
        onChange={(e) => onSearchInput(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => {
          if (results.length > 0) setOpen(true)
        }}
        // Close on blur (click-outside / tab-away) so a stale dropdown doesn't
        // linger. Option selection uses onMouseDown + preventDefault, which
        // keeps focus on the input, so picking a result does NOT fire this.
        onBlur={() => setOpen(false)}
        placeholder={t('login.schoolSearch.placeholder')}
        autoComplete="off"
        tabIndex={tabIndex}
        data-testid={`${testIdPrefix}-input`}
      />
      {open && searchError === 'unavailable' ? (
        // #1122: a transient search failure renders a DISTINCT alert instead
        // of the listbox — never the "no matching schools" hint, which would
        // wrongly read as "your school isn't in our system" and strand a
        // first-time student. role="alert" announces it to AT, and the copy
        // steers them to the manual-code fallback (the toggle just below).
        // Kept outside the listbox <ul> so it isn't a phantom listbox option.
        <div
          className={`${s.dropdown} ${s.alert}`}
          role="alert"
          data-testid={`${testIdPrefix}-error`}
        >
          {t('login.schoolSearch.searchUnavailable')}
        </div>
      ) : open ? (
        <ul
          id={listboxId}
          role="listbox"
          className={s.dropdown}
          data-testid={`${testIdPrefix}-results`}
        >
          {loading && results.length === 0 ? (
            <li className={s.hint} role="presentation">
              {t('login.schoolSearch.loading')}
            </li>
          ) : results.length === 0 ? (
            <li className={s.hint} role="presentation">
              {t('login.schoolSearch.noResults')}
            </li>
          ) : (
            results.map((r, i) => (
              <li
                key={r.login_code}
                id={`${listboxId}-opt-${i}`}
                role="option"
                aria-selected={i === activeIndex}
                className={`${s.option} ${i === activeIndex ? s.optionActive : ''}`}
                // onMouseDown (not onClick) so the pick fires before the
                // input's blur closes the dropdown.
                onMouseDown={(e) => {
                  e.preventDefault()
                  pick(r)
                }}
                onMouseEnter={() => setActiveIndex(i)}
                data-testid={`${testIdPrefix}-option`}
              >
                <span className={s.optionName}>{r.name}</span>
                {r.location && (
                  <span className={s.optionLoc}>{r.location}</span>
                )}
              </li>
            ))
          )}
        </ul>
      ) : null}
      <button
        type="button"
        // #1122: when search is unavailable the manual-code path is the only
        // way forward, so promote the toggle to the dominant CTA rather than
        // the usual quiet underline.
        className={`${s.toggle} ${searchError ? s.toggleEmphasis : ''}`}
        onClick={switchToManual}
        tabIndex={tabIndex}
        data-testid={`${testIdPrefix}-to-manual`}
      >
        {t('login.schoolSearch.manualToggle')}
      </button>
    </div>
  )
}
